import { alpha } from "@mui/material/styles";

// ----------------------------------------------------------------------

export type ColorSchema =
  | "primary"
  | "secondary"
  | "info"
  | "success"
  | "warning"
  | "error";

declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface TypeText {
    stroke: string;
  }

  interface Palette {
    LightText: {
      primary: string;
      secondary: string;
      disabled: string;
      stroke: string;
    };
    NightText: {
      primary: string;
      secondary: string;
      disabled: string;
      stroke: string;
    };
  }
}

// SETUP COLORS

const GREY = {
  0: "#FFFFFF",
  100: "#F8F9FB",
  200: "#E8ECF2",
  300: "#CACFD8",
  400: "#A3A8B1",
  500: "#71757E",
  600: "#656872",
  700: "#494C54",
  800: "#383A3F",
  900: "#1F2025",
  1000: "#000",
};

const PRIMARY = {
  lighter: "#D8E7FE",
  light: "#89AFFC",
  main: "#3C6FF5",
  dark: "#1E3EB0",
  darker: "#0B1C75",
  contrastText: "#fff",
};

const SECONDARY = {
  lighter: "#F1FCB6",
  light: "#D4ED72",
  main: "#BEE246",
  dark: "#7FA223",
  darker: "#4D6C0D",
  contrastText: "#fff",
};

const INFO = {
  lighter: "#CAFDF5",
  light: "#61F3F3",
  main: "#00B8D9",
  dark: "#006C9C",
  darker: "#003768",
  contrastText: "#fff",
  grey: "#f5f6f7",
};

const SUCCESS = {
  lighter: "#D8FBDE",
  light: "#86E8AB",
  main: "#36B37E",
  dark: "#1B806A",
  darker: "#0A5554",
  contrastText: "#fff",
};

const WARNING = {
  lighter: "#FFF5CC",
  light: "#FFD666",
  main: "#FFAB00",
  dark: "#B76E00",
  darker: "#7A4100",
  contrastText: GREY[800],
};

const ERROR = {
  lighter: "#FFE9D5",
  light: "#FFAC82",
  main: "#FF5630",
  dark: "#B71D18",
  darker: "#7A0916",
  contrastText: "#fff",
};

const LightText = {
  primary: "#1F2025",
  secondary: GREY[500],
  disabled: "#B3B9C5",
  stroke: GREY[200],
};

const NightText = {
  primary: "#fff",
  secondary: "#9EA2B6",
  disabled: "#53555E",
  stroke: "#2E2F34",
};

const COMMON = {
  common: { black: "#000", white: "#fff" },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
  LightText,
  NightText,
};

export default function palette(themeMode: "light" | "dark") {
  const light = {
    ...COMMON,
    mode: "light",
    text: LightText,
    background: { paper: "#fff", default: "#fff", neutral: GREY[200] },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: "dark",
    text: NightText,
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.16),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  } as const;

  return themeMode === "light" ? light : dark;
}
